// src/app/AppSidebar.tsx
/**
 * The primary navigation sidebar: brand header, collapse toggle, the nav item list and the
 * footer actions (patch notes, preferences, logout).
 *
 * Extracted from AuthedApp.tsx (plans/monolith-migration-plan.md §11.12, Batch 21). The shell
 * had 164 lines of sidebar markup inline between the layout div and the main column.
 */
import type { TFunction } from 'i18next';
import { LogOut, PanelLeftClose, PanelLeftOpen, Settings, Sparkles, X } from 'lucide-react';
import { NavItemsList } from './AppNav';
import { PATCH_NOTES, logGlobalAction } from '../constants';

export interface AppSidebarProps {
    t: TFunction;
    user: { email?: string; name?: string; tenantId?: string | null };
    allowedTabs: string[];
    activeTab: string;
    handleNavClick: (tab: string) => void;
    expandedGroups: Record<string, boolean>;
    toggleGroup: (group: string) => void;
    hasUnreadSupportTickets: boolean;
    hasPendingHandover: boolean;
    isEffectiveCollapsed: boolean;
    isSidebarCollapsed: boolean;
    setIsSidebarCollapsed: (v: boolean) => void;
    isMobileMenuOpen: boolean;
    setIsMobileMenuOpen: (v: boolean) => void;
    setIsPreferencesOpen: (v: boolean) => void;
    hasNewPatchNote: boolean;
    handleOpenPatchNotes: () => void;
    getRoleData: (role: string | null | undefined) => { name: string; badge: string; tabs: string[] } | null;
    userRole: string | null;
    signOut: () => Promise<void>;
    chatUnreadCount: number;
    tenantConfig: { features?: Record<string, boolean> } | null;
}

export function AppSidebar({
    t,
    user,
    allowedTabs,
    activeTab,
    handleNavClick,
    expandedGroups,
    toggleGroup,
    hasUnreadSupportTickets,
    hasPendingHandover,
    isEffectiveCollapsed,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    setIsPreferencesOpen,
    hasNewPatchNote,
    handleOpenPatchNotes,
    getRoleData,
    userRole,
    signOut,
    chatUnreadCount,
    tenantConfig,
}: AppSidebarProps) {
    return (
    <aside
        className={`hub-sidebar bg-white dark:bg-surface z-300 md:z-40 flex flex-col h-dvh max-h-dvh overflow-y-auto md:overflow-hidden transition-all duration-300 ease-in-out ${isEffectiveCollapsed ? "collapsed" : ""} ${isMobileMenuOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full md:translate-x-0"} print:hidden`}
    >
        <div className={`shrink-0 ${isEffectiveCollapsed ? "p-3 flex flex-col items-center gap-3" : "p-4 md:p-6 flex flex-col gap-1"} border-b border-slate-100 dark:border-slate-800`}>
            {!isEffectiveCollapsed ? (
                <>
                    <div className="flex items-center justify-between w-full">
                        <h1 className="text-xl font-bold tracking-tight text-[#4a5929] dark:text-[#a5c263]">
                            Adapt Hub
                        </h1>

                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                                className="hidden md:flex text-slate-400 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
                                title={isSidebarCollapsed ? "Sidebar erweitern" : "Sidebar einklappen"}
                            >
                                {isSidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
                            </button>

                            <button
                                className="md:hidden text-slate-800 dark:text-slate-200 hover:text-black dark:hover:text-white p-1 bg-slate-50 dark:bg-slate-800 rounded-lg"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                        <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${getRoleData(userRole)?.badge}`}
                        >
                            {getRoleData(userRole)?.name}
                        </span>
                    </div>
                </>
            ) : (
                <>
                    <div className="w-8 h-8 rounded-xl bg-[#859F4A]/10 flex items-center justify-center text-[#859F4A] font-bold text-sm select-none">
                        AH
                    </div>
                    <div className="flex flex-col items-center gap-1">
                        <button
                            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                            className="hidden md:flex text-slate-400 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
                            title={isSidebarCollapsed ? "Sidebar erweitern" : "Sidebar einklappen"}
                        >
                            {isSidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
                        </button>

                        <button
                            className="md:hidden text-slate-800 dark:text-slate-200 hover:text-black dark:hover:text-white p-1 bg-slate-50 dark:bg-slate-800 rounded-lg"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            <X size={20} />
                        </button>
                    </div>
                </>
            )}
        </div>

        <nav className={`flex-1 min-h-0 overflow-y-auto overscroll-contain ${isEffectiveCollapsed ? "px-1 py-4 flex flex-col items-center" : "px-4 py-4"} space-y-1`}>
            <NavItemsList
                user={user}
                allowedTabs={allowedTabs}
                expandedGroups={expandedGroups}
                activeTab={activeTab}
                onNavClick={handleNavClick}
                toggleGroup={toggleGroup}
                hasUnreadSupportTickets={hasUnreadSupportTickets}
                hasPendingHandover={hasPendingHandover}
                chatUnreadCount={chatUnreadCount}
                tenantConfig={tenantConfig}
                isCollapsed={isEffectiveCollapsed}
            />
        </nav>

        <div className={`shrink-0 ${isEffectiveCollapsed ? "p-3 flex flex-col items-center gap-3.5" : "p-4"} pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)] border-t border-slate-100 dark:border-slate-800`}>
            {!isEffectiveCollapsed ? (
                <div className="mt-2 md:mt-4 flex flex-col gap-2">
                    <button
                        onClick={handleOpenPatchNotes}
                        className="group w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg border border-[#859F4A]/30 dark:border-[#859F4A]/40 bg-[#f3f5ef] dark:bg-slate-800/80 hover:bg-[#e6ebd9] dark:hover:bg-slate-800 hover:border-[#859F4A]/60 transition-all cursor-pointer shadow-xs whitespace-nowrap"
                        title={t('patchNotes.whatsNewTooltip', 'Klicke hier, um die Patch-Notes zu lesen')}
                    >
                        <Sparkles size={12} className="text-[#859F4A] group-hover:scale-110 transition-transform shrink-0" />
                        <span className="text-[10px] font-bold text-[#5d7335] dark:text-[#a5c263] uppercase tracking-wide">
                            {t('patchNotes.whatsNew', 'Was ist neu?')}
                        </span>
                        <span className="text-[9px] font-bold text-white bg-[#859F4A] px-1.5 py-0.5 rounded">
                            {PATCH_NOTES[0]?.version || "v1.4.0"}
                        </span>
                        {hasNewPatchNote && (
                            <span className="relative flex h-2 w-2 ml-0.5 shrink-0" title={t('patchNotes.labels.new', 'Neu')}>
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                            </span>
                        )}
                    </button>
                    <div className="px-2">
                        <p className="text-xs font-bold text-black dark:text-slate-100 truncate">
                            {user?.name || "Unbekannt"}
                        </p>
                        <p className="text-[10px] text-slate-800 dark:text-slate-400 truncate">
                            Eingeloggt
                        </p>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col items-center gap-3">
                    <button
                        onClick={handleOpenPatchNotes}
                        className={`group flex flex-col items-center justify-center gap-0.5 p-2 rounded-lg border transition-all cursor-pointer relative ${hasNewPatchNote
                            ? "border-[#859F4A]/60 bg-[#f3f5ef] dark:bg-slate-800/80 text-[#5d7335] dark:text-[#a5c263] shadow-xs"
                            : "border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-400 hover:text-[#859F4A] hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-[#859F4A]/40"
                            }`}
                        title={t('patchNotes.whatsNewTooltip', 'Klicke hier, um die Patch-Notes zu lesen')}
                    >
                        <div className="relative">
                            <Sparkles size={18} className="group-hover:scale-110 transition-transform" />
                            {hasNewPatchNote && (
                                <span className="absolute -top-1 -right-1 flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                                </span>
                            )}
                        </div>
                        <span className="text-[8px] font-bold uppercase tracking-wide">
                            {t('patchNotes.whatsNew', 'Was ist neu?')}
                        </span>
                    </button>
                </div>
            )}

            <button
                onClick={() => {
                    setIsPreferencesOpen(true);
                    setIsMobileMenuOpen(false);
                }}
                className="md:hidden flex items-center gap-2 bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-300 font-bold text-sm w-full px-3 py-2.5 rounded-xl transition-colors mb-2 border border-slate-100 dark:border-slate-700"
            >
                <Settings size={16} className="text-[#859F4A]" /> {t('preferences.title', 'Präferenzen')}
            </button>

            <button
                onClick={() => {
                    logGlobalAction(
                        user,
                        "Logout",
                        "Benutzer hat sich abgemeldet.",
                    );
                    signOut();
                    localStorage.removeItem("adapthub_login_method");
                    localStorage.removeItem("adapthub_is_guest");
                    localStorage.removeItem("adapthub_guest_tenant");
                }}
                title={isEffectiveCollapsed ? t('common.logout') : undefined}
                className={`flex items-center ${isEffectiveCollapsed ? "justify-center" : "gap-2"} bg-slate-50 dark:bg-slate-800/80 hover:bg-red-50 dark:hover:bg-red-950/40 text-slate-800 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 font-bold text-sm w-full px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            >
                <LogOut size={16} />
                {!isEffectiveCollapsed && <span>{t('common.logout')}</span>}
            </button>
        </div>
    </aside>
    );
}
